import React from 'react';

import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import {
    CONTRACT_TERM_HELP_INFO_MAP,
    ContractTermName,
    HelpInfoContentType,
    IList,
    IListItem,
    IParagraph
} from 'src/constants/contractTermHelpInfo';

import './ContractTermHelpInfoTemplate.scss';

interface IContractTermHelpInfoTemplateProps {
    contractTermName: keyof typeof ContractTermName;
}

export const ContractTermHelpInfoTemplate = ({ contractTermName }: IContractTermHelpInfoTemplateProps) => {
    const helpInfoObj = CONTRACT_TERM_HELP_INFO_MAP.get(contractTermName);
    if (!helpInfoObj) return null;

    const renderListItem = (listItem: IListItem, index: number) => {
        const title = listItem[HelpInfoContentType.ListItemTitle];
        const body = listItem[HelpInfoContentType.ListItemBody];

        return (
            <li key={index}>
                {title ? (
                    <span>
                        <Text tag="span" type="h100">
                            {title}
                        </Text>
                        <Text tag="span">: {body}</Text>
                    </span>
                ) : (
                    <Text>{body}</Text>
                )}
                {listItem.children && (
                    <ul className="contract-term-help-info-list">
                        {listItem.children.body.map((childItem, childIndex) => (
                            <li key={`${index}-${childIndex}`}>
                                <Text>{childItem[HelpInfoContentType.ListItemBody]}</Text>
                            </li>
                        ))}
                    </ul>
                )}
            </li>
        );
    };

    const renderContent = (contentObj: IParagraph | IList) => {
        if (contentObj.type === HelpInfoContentType.Paragraph) {
            return <Text>{contentObj.body}</Text>;
        }

        if (contentObj.type === HelpInfoContentType.List) {
            return (
                <ul className="contract-term-help-info-list">
                    {contentObj.body.map((listItem, index) => renderListItem(listItem, index))}
                </ul>
            );
        }
    };

    return (
        <>
            <Row>
                <Heading level={4}>Term Name</Heading>
            </Row>
            <Row>
                <Text>{helpInfoObj.termName}</Text>
            </Row>

            <Row>
                <Heading level={4}>Term Description</Heading>
            </Row>
            <Column>
                {helpInfoObj.termDescription.map((descriptionItem, index) => (
                    <div key={`desc-${index}`}>{renderContent(descriptionItem)}</div>
                ))}
            </Column>
            {helpInfoObj.sections?.map((sectionItem, sectionIndex) => (
                <Column key={`section-${sectionIndex}`}>
                    {sectionItem.sectionTitle && (
                        <Row>
                            <Heading level={4}>{sectionItem.sectionTitle}</Heading>
                        </Row>
                    )}
                    {sectionItem.sectionContent.map((contentItem, contentIndex) => (
                        <div key={`content-${sectionIndex}-${contentIndex}`}>{renderContent(contentItem)}</div>
                    ))}
                </Column>
            ))}
        </>
    );
};
